import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  IFormbuilderFieldTypeConfiguration,
  IGenericDataFetchFieldsConfigFormModel,
  ITableWrapperConfigurationFormModel,
} from '../../../models/form-builder-types-configurations.model';
import { FormBuilderService } from '../../../services/form-builder.service';
import { IFormFieldsValidationModel } from '../../../models/form-fields-formly-validation.model';
import { IFormFieldsDisplayModel } from '../../../models/form-fields-formly-display.model';
import { EIremboFormlyFieldTypes } from '@irembo-andela/irembogov3-common';
import {
  getDefaultBaseSettingsConfigFormly,
  generateDatasetSettingsConfig,
  genericDataFieldFieldParseIntoSettingsFormModel,
  genericDataFieldUpdateFromSettingsFormModel,
  baseFieldUpdateFromSettingsFormModel,
} from '../form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import { setRequiredValidatorUpdateFieldFromModel } from '../form-fields-formly-validations/form-fields-formly-validation-field-set-from-model.util';
import { parseFieldIntoDisplayFormModel } from '../form-fields-formly-display/form-fields-formly-display-field-parse-to-model.util';
import { setDisplayHideExpressionFromModel } from '../form-fields-formly-display/form-fields-formly-display-field-set-from-model.util';
import { parseRequiredValidatorParseFieldIntoModel } from '../form-fields-formly-validations/form-fields-formly-validation-field-parse-to-model.util';

export const defaultTableWrapperConfigFormModel: ITableWrapperConfigurationFormModel =
  {
    fieldLabel: null,
    placeholder: null,
    hint: null,
    tooltip: null,
    internalDescription: null,
    internal: true,
    tableSection: {
      columns: [],
      selectionType: 'none',
      isSelectable: false,
      maxSelectable: 1,
      selectionOutputKey: '',
      selectionOutputPath: '',
      isSortVisible: true,
      isSearchVisible: true,
      searchPlaceholder: 'Search',
      paginationVisible: true,
      paginationInsideTable: false,
      pageSizes: [10, 50, 100],
      tableClass: 'table',
      roundedTable: true,
      wrapperTopPaddingClass: 'pt-4',
      wrapperTopMarginClass: 'mt-4',
      noDataMessage: 'No data available',
      showDropdown: false,
    },
    datasetSection: {
      url: null,
      dataField: null,
      customUrl: null,
      httpMethod: 'GET',
      useBaseUrl: true,
      code: '',
      params: {
        size: 10,
        page: 0,
        datasetId: '',
      },
      searching: false,
      searchField: undefined,
      headers: {},
    },
  };

export const tableWrapperFieldGetConfigurationConfig = (
  formBuilderService: FormBuilderService
): IFormbuilderFieldTypeConfiguration => {
  return {
    settings: {
      formModel: defaultTableWrapperConfigFormModel,
      formlyForm: [
        ...getDefaultBaseSettingsConfigFormly(formBuilderService),
        {
          key: 'tableSection',
          wrappers: ['panel-wrapper'],
          props: {
            label: 'Table Configuration',
          },
          fieldGroup: [
            {
              key: 'columns',
              type: EIremboFormlyFieldTypes.customexpansionrepeater,
              wrappers: ['custom-expansion-panel-with-icon'],
              props: {
                label: 'Columns',
                addText: 'Add Column',
                hideLabel: true,
                required: false,
                titleFieldName: 'label',
              },
              fieldArray: {
                fieldGroup: [
                  {
                    key: 'label',
                    type: EIremboFormlyFieldTypes.input,
                    props: {
                      label: 'Column Label',
                      required: true,
                    },
                  },
                  {
                    key: 'key',
                    type: EIremboFormlyFieldTypes.input,
                    props: {
                      label: 'Column Key',
                      required: true,
                    },
                  },
                  {
                    key: 'sortable',
                    type: EIremboFormlyFieldTypes.checkbox,
                    props: {
                      label: 'Sortable',
                      formCheck: 'switch',
                    },
                  },
                  {
                    key: 'width',
                    type: EIremboFormlyFieldTypes.input,
                    props: {
                      label: 'Column Width',
                      placeholder: 'e.g., 100px, 10%, fit-content',
                    },
                  },
                  {
                    key: 'nowrap',
                    type: EIremboFormlyFieldTypes.checkbox,
                    props: {
                      label: 'No Text Wrap',
                      formCheck: 'switch',
                    },
                  },
                ],
              },
            },
            {
              key: 'isSelectable',
              type: EIremboFormlyFieldTypes.checkbox,
              props: {
                label: 'Enable Row Selection',
                formCheck: 'switch',
              },
            },
            {
              key: 'maxSelectable',
              type: EIremboFormlyFieldTypes.input,
              props: {
                type: 'number',
                label: 'Maximum Selectable Rows',
                min: 1,
              },
              expressions: {
                hide: '!model.tableSection?.isSelectable',
              },
            },
            {
              key: 'selectionOutputPath',
              type: EIremboFormlyFieldTypes.input,
              props: {
                label: 'Selection Output Path',
                description:
                  'Dot notation path to extract from selected row (e.g., user.id)',
              },
              expressions: {
                hide: '!model.tableSection?.isSelectable',
              },
            },
            {
              key: 'isSortVisible',
              type: EIremboFormlyFieldTypes.checkbox,
              props: {
                label: 'Show Sort Controls',
                formCheck: 'switch',
              },
            },
            {
              key: 'isSearchVisible',
              type: EIremboFormlyFieldTypes.checkbox,
              props: {
                label: 'Show Search',
                formCheck: 'switch',
              },
            },
            {
              key: 'searchPlaceholder',
              type: EIremboFormlyFieldTypes.input,
              props: {
                label: 'Search Placeholder',
              },
              expressions: {
                hide: '!model.tableSection?.isSearchVisible',
              },
            },
            {
              key: 'paginationVisible',
              type: EIremboFormlyFieldTypes.checkbox,
              props: {
                label: 'Show Pagination',
                formCheck: 'switch',
              },
            },
            {
              key: 'paginationInsideTable',
              type: EIremboFormlyFieldTypes.checkbox,
              props: {
                label: 'Pagination Inside Table',
                formCheck: 'switch',
              },
              expressions: {
                hide: '!model.tableSection?.paginationVisible',
              },
            },
            {
              key: 'pageSizes',
              type: EIremboFormlyFieldTypes.input,
              props: {
                label: 'Page Sizes',
                description:
                  'Enter numbers separated by commas (e.g., 10,25,50)',
                placeholder: '10,50,100',
              },
              parsers: [
                (value: string) =>
                  value
                    .split(',')
                    .map(Number)
                    .filter(n => !isNaN(n)),
              ],
              expressions: {
                hide: '!model.tableSection?.paginationVisible',
              },
            },
            {
              key: 'tableClass',
              type: EIremboFormlyFieldTypes.input,
              props: {
                label: 'Table CSS Class',
                placeholder: 'table table-striped',
              },
            },
            {
              key: 'roundedTable',
              type: EIremboFormlyFieldTypes.checkbox,
              props: {
                label: 'Rounded Table',
                formCheck: 'switch',
              },
            },
          ],
        },
        generateDatasetSettingsConfig(),
        {
          key: 'datasetSection',
          wrappers: ['panel-wrapper'],
          props: {
            label: 'Search Configuration',
          },
          fieldGroup: [
            {
              key: 'searching',
              type: EIremboFormlyFieldTypes.checkbox,
              props: {
                label: 'Enable Search',
                formCheck: 'switch',
              },
            },
            {
              key: 'searchField',
              type: EIremboFormlyFieldTypes.input,
              props: {
                label: 'Search Field',
                description: 'Field to use for searching',
              },
              expressions: {
                hide: '!model.datasetSection?.searching',
              },
            },
          ],
        },
      ],
    },
    validations: {
      formModel: {
        required: false,
      },
      formlyForm: [
        {
          key: 'validation',
          wrappers: ['panel-wrapper'],
          props: {
            label: 'Validation',
          },
          fieldGroup: [
            {
              key: 'required',
              type: EIremboFormlyFieldTypes.checkbox,
              props: {
                label: 'Required',
                formCheck: 'switch',
              },
            },
          ],
        },
      ],
    },
    display: {
      formModel: {},
      formlyForm: [],
    },
  };
};

export const tableWrapperFieldParseIntoSettingsFormModel = (
  field: FormlyFieldConfig
): ITableWrapperConfigurationFormModel => {
  const model: ITableWrapperConfigurationFormModel = {
    ...defaultTableWrapperConfigFormModel,
  };

  if (field.props) {
    // Table section
    model.tableSection = {
      ...model.tableSection,
      columns: field.props['columns'] || [],
      isSelectable: field.props['isSelectable'] || false,
      maxSelectable: field.props['maxSelectable'] || 1,
      selectionOutputKey: field.props['selectionOutputKey'] || field.key,
      selectionOutputPath: field.props['selectionOutputPath'] || '',
      isSortVisible: field.props['isSortVisible'] ?? true,
      isSearchVisible: field.props['isSearchVisible'] ?? true,
      searchPlaceholder: field.props['searchPlaceholder'] || 'Search',
      paginationVisible: field.props['paginationVisible'] ?? true,
      paginationInsideTable: field.props['paginationInsideTable'] ?? false,
      pageSizes: field.props['pageSizes'] || [10, 50, 100],
      tableClass: field.props['tableClass'] || 'table',
      roundedTable: field.props['roundedTable'] ?? true,
      noDataMessage: field.props['noDataMessage'] || 'No data available',
      showDropdown: field.props['showDropdown'] ?? false,
    };
  }

  return {
    ...model,
    ...genericDataFieldFieldParseIntoSettingsFormModel(field),
  };
};

export const tableWrapperFieldUpdateFromSettingsFormModel = (
  model: ITableWrapperConfigurationFormModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  field.props = field.props || {};

  // Base properties
  field.props['label'] = model.fieldLabel || undefined;
  field.props['placeholder'] = model.placeholder || undefined;
  field.props['hint'] = model.hint;
  field.props['tooltip'] = model.tooltip;
  field.props['internalDescription'] = model.internalDescription;
  field.props['internal'] = model.internal;

  // Table section properties
  field.props['columns'] = model.tableSection.columns;
  field.props['isSelectable'] = model.tableSection.isSelectable;
  field.props['maxSelectable'] = model.tableSection.maxSelectable;
  field.props['selectionOutputKey'] = model.tableSection.selectionOutputKey;
  field.props['selectionOutputPath'] = model.tableSection.selectionOutputPath;
  field.props['isSortVisible'] = model.tableSection.isSortVisible;
  field.props['isSearchVisible'] = model.tableSection.isSearchVisible;
  field.props['searchPlaceholder'] = model.tableSection.searchPlaceholder;
  field.props['paginationVisible'] = model.tableSection.paginationVisible;
  field.props['paginationInsideTable'] =
    model.tableSection.paginationInsideTable;
  field.props['pageSizes'] = model.tableSection.pageSizes;
  field.props['tableClass'] = model.tableSection.tableClass;
  field.props['roundedTable'] = model.tableSection.roundedTable;
  field.props['showDropdown'] = model.tableSection.showDropdown;

  const dataset = field.props['dataset'] || {};
  const params: Record<string, any> = {};

  params['datasetId'] = model.datasetSection?.code;
  dataset['dataField'] = 'data.content';
  dataset['url'] = '/admin/v1/dataset-items';
  dataset['pagination'] = true;
  dataset['searching'] = true;
  params['page'] = 0;
  params['size'] = 10;
  dataset['params'] = params;
  field.props['dataset'] = dataset;
  field.props['code'] = model.datasetSection?.code ?? undefined;

  return {
    ...field,
    ...baseFieldUpdateFromSettingsFormModel(model, field),
  };
};

export const tableWrapperFieldParseIntoValidationFormModel = (
  field: FormlyFieldConfig
): IFormFieldsValidationModel => {
  let model: IFormFieldsValidationModel = {};
  model = parseRequiredValidatorParseFieldIntoModel(model, field);

  if (field.props) {
    model.tableValidation = {
      required: !!field.validators?.['required'],
      minSelections: field.props['minSelections'] || 0,
      maxSelections: field.props['maxSelections'] || 0,
    };
  }

  return model;
};

export const tableWrapperFieldUpdateFromValidationFormModel = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  field = setRequiredValidatorUpdateFieldFromModel(model, field);

  if (model.tableValidation) {
    field.props = field.props || {};
    field.props['minSelections'] = model.tableValidation.minSelections;
    field.props['maxSelections'] = model.tableValidation.maxSelections;

    field.validation = field.validation || {};
    field.validation.messages = field.validation.messages || {};
    // field.validation.messages['selection'] = model.tableValidation.selectionMessage;
  }

  return field;
};

export const tableWrapperFieldParseIntoDisplayFormModel = (
  field: FormlyFieldConfig
): IFormFieldsDisplayModel => {
  return parseFieldIntoDisplayFormModel(field);
};

export const tableWrapperFieldUpdateFromDisplayFormModel = (
  model: IFormFieldsDisplayModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  return setDisplayHideExpressionFromModel(model, field);
};
